import * as Yup from 'yup';
import { format } from 'date-fns';
import {
  getRandomCountry,
  generateRandomName,
  generateRandomDateOfBirth,
  generateRandomIncidentDate,
  getRandomItem,
  generateRandomProvinceOrState,
 getRandomClaimantSector,
} from '../../utils/randomDataGenerator';
import {
  genderOptions as genders,
  educationLevels,
  employmentStatusOptions as employmentStatuses
} from '../OptionDefinitions';
import ClaimantInformationStep from '../CommonSteps/ClaimantInformationStep';
import DatesAndRatesStep from '../CommonSteps/DatesAndRatesStep';
import LifeCarePlanDetailsStep from './steps/LifeCarePlanDetailsStep';
import ReviewStep from '../CommonSteps/ReviewStep';
import ReportStep from './steps/ReportStep';
import ClaimantInformation from '../../components/Report/sections/ClaimantInformation';
import ExecutiveSummary from '../../components/Report/sections/ExecutiveSummary';
import LifeCarePlanSummary from '../../components/Report/sections/LifeCarePlanSummary';
import ReportAppendices from '../../components/Report/sections/ReportAppendices';
import { lifeCarePlanCategoryConfig } from './lifeCarePlanCategoryConfig';

const generateValidationSchema = () => {
  const categorySchemas = Object.entries(lifeCarePlanCategoryConfig).reduce((acc, [category, config]) => {
    const fieldSchemas = config.columns.reduce((schema, column) => {
      if (column.readOnly) return schema;

      let validator;
      switch (column.type) {
        case 'text':
          validator = Yup.string();
          break;
        case 'number':
          validator = Yup.number().positive(`${column.headerName} must be positive.`);
          break;
        case 'date':
          validator = Yup.string(); // Dates are strings in 'yyyy-MM-dd' format
          break;
        default:
          validator = Yup.mixed();
      }

      if (column.required) {
        validator = validator.required(`${column.headerName} is required.`);
      }

      schema[column.field] = validator;
      return schema;
    }, {});

    acc[category] = Yup.array().of(Yup.object(fieldSchemas));
    return acc;
  }, {});

  return Yup.object({
    lifeCarePlan: Yup.object(categorySchemas),
  });
};

export const getDefaultValues= () => {
    console.log('Getting default values for life care plan');

    if (process.env.NODE_ENV === 'development') {
      const randomCountry=  getRandomCountry();
      const randomProvinceOrState = generateRandomProvinceOrState(randomCountry);
      return {
        fullName: generateRandomName(),
        dateOfBirth: generateRandomDateOfBirth(),
        incidentDate: generateRandomIncidentDate(),
        gender: getRandomItem(genders),
        educationLevel: getRandomItem(educationLevels),
        employmentStatus: getRandomItem(employmentStatuses),
        claimantSector: getRandomClaimantSector(randomCountry),
        country: randomCountry,
        provinceOrState: randomProvinceOrState,
        valuationDate: format(new Date(), 'yyyy-MM-dd'),
        discountRate: 0.035,
        growthRate: 0.0275,
        useCustomValues: false,
      };
    }
    return getInitialValues();
}

export const getInitialValues = () => {
  return {
    fullName: '',
    dateOfBirth: '',
    incidentDate: '',
    gender: '',
    educationLevel: '',
    employmentStatus: '',
    claimantSector: '',
    country: '',
    provinceOrState: '',
    valuationDate: format(new Date(), 'yyyy-MM-dd'),
    useCustomValues: false,
    discountRate: 0.035,
    growthRate: 0.0275,
    useCustomValues: false,
  }
}

export const lifeCarePlanConfig = {
  id: 'life-care-plan',
  name: 'Life Care Plan',
  enabled: true,
  defaultValues: () => {
    return getDefaultValues();
  },
  steps: [
    {
      id: 'claimant-information',
      name: 'Claimant Information',
      component: ClaimantInformationStep,
      validationSchema: Yup.object({
        fullName: Yup.string().required('Required'),
        incidentDate: Yup.date()
          .required('Incident date is required')
          .max(new Date(), 'Incident date cannot be in the future'),
        dateOfBirth: Yup.date()
          .required('Date of birth is required')
          .max(new Date(), 'Date of birth cannot be in the future')
          .test(
            'is-not-too-old',
            'Claimant cannot be more than 120 years old',
            (value) => {
              if (!value) return true;
              const age = new Date().getFullYear() - new Date(value).getFullYear();
              return age <= 120;
            }
          ),
        country: Yup.string().required('Country is required'),
        provinceOrState: Yup.string().required('Province or state is required'),
      }),
    },
    {
      id: 'dates-and-rates',
      name: 'Dates and Rates',
      component: DatesAndRatesStep,
      validationSchema: Yup.object({
        valuationDate: Yup.date()
        .required('Valuation date is required')
      }),
    },
    {
      id: 'life-care-plan-details',
      name: 'Life Care Plan Details',
      component: LifeCarePlanDetailsStep,
      validationSchema: generateValidationSchema(),
    },
    {
      id: 'review',
      name: 'Review',
      component: ReviewStep,
    },
    {
      id: 'report',
      name: 'Report',
      component: ReportStep,
    },
  ],
  reportSections: [
    { id: 'claimant-info', component: ClaimantInformation },
    { id: 'executive-summary', component: ExecutiveSummary },
    { id: 'life-care-plan-summary', component: LifeCarePlanSummary },
    { id: 'appendices', component: ReportAppendices },
  ],
};